import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-bone border border-forest/25 px-4 py-3 text-sm text-ink placeholder:text-moss/50 focus:outline-none focus:border-terra focus:ring-1 focus:ring-terra transition";

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received — we reply within two working days.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 border-t border-forest/15" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <span className="text-xs font-bold tracking-[0.3em] uppercase text-terra">Say Hello</span>
          <h2 className="font-serif font-bold tracking-tighter text-forest text-4xl sm:text-5xl mt-4" data-testid="contact-heading">
            Questions? <span className="italic text-terra">Write</span> to us.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-moss max-w-md">
            Whether you're a school, a sponsor, or simply curious — our team reads
            every message and replies within two working days.
          </p>
          <div className="mt-10 space-y-3 text-sm text-forest">
            <p><span className="font-bold text-terra">Email</span> — info_lli@learninglightsinitiative.org</p>
            <p><span className="font-bold text-terra">Phone</span> — +91 93925 90701</p>
            <p><span className="font-bold text-terra">Field Office</span> — Plot no. 57, Sai Ram Nagar, Puppalaguda, Hyderabad 500089</p>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7 space-y-4"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="contact-name-input" />
            <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="contact-email-input" />
          </div>
          <input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} data-testid="contact-subject-input" />
          <textarea required rows={5} placeholder="Your message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} data-testid="contact-message-input" />
          <button type="submit" disabled={busy} data-testid="contact-submit-btn"
            className="group bg-terra text-bone font-bold px-8 py-4 flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:translate-y-0">
            {busy ? "Sending…" : "Send Message"}
            <PaperPlaneTilt size={18} className="transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
          </button>
        </motion.form>
      </div>
    </section>
  );
};
